import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { WishlistItem, useWishlist } from '../../contexts/WishlistContext';
import { SUPPORTED_CURRENCIES, getDefaultCurrency } from '../../utils/currency';
import { ozToMl, mlToOz } from '../../utils/sizeConversion';
import Button from '../ui/Button';
import Input from '../ui/Input';

interface WishlistFormProps {
  item?: WishlistItem | null;
  onClose: () => void;
  onSuccess: () => void;
}

const WishlistForm: React.FC<WishlistFormProps> = ({ item, onClose, onSuccess }) => {
  const { createWishlistItem, updateWishlistItem, isLoading } = useWishlist();
  
  const [formData, setFormData] = useState({
    house: '',
    name: '',
    concentration: '',
    size_oz: 0,
    size_ml: 0,
    notes_top: '',
    notes_middle: '',
    notes_base: '',
    price: 0,
    currency: getDefaultCurrency(),
    retailer: '',
    retailer_link: '',
    notes: '',
    is_gift_idea: false,
    priority: 'medium' as 'low' | 'medium' | 'high'
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [generalError, setGeneralError] = useState('');

  // Populate form if editing
  useEffect(() => {
    if (item) {
      setFormData({
        house: item.house || '',
        name: item.name || '',
        concentration: item.concentration || '',
        size_oz: item.size_oz || 0,
        size_ml: item.size_ml || 0,
        notes_top: item.notes_top || '',
        notes_middle: item.notes_middle || '',
        notes_base: item.notes_base || '',
        price: item.price || 0,
        currency: item.currency || 'USD',
        retailer: item.retailer || '',
        retailer_link: item.retailer_link || '',
        notes: item.notes || '',
        is_gift_idea: item.is_gift_idea || false,
        priority: item.priority || 'medium'
      });
    }
  }, [item]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    let newValue: any = type === 'checkbox' ? checked : value;
    let updateObject: any = { [name]: newValue };

    // Handle size conversion
    if (name === 'size_oz' && value) {
      const ozValue = parseFloat(value);
      if (!isNaN(ozValue)) {
        updateObject.size_ml = ozToMl(ozValue); // Convert oz to ml using standard sizes
      }
    } else if (name === 'size_ml' && value) {
      const mlValue = parseFloat(value);
      if (!isNaN(mlValue)) {
        updateObject.size_oz = mlToOz(mlValue); // Convert ml to oz using standard sizes
      }
    }

    setFormData(prev => ({ ...prev, ...updateObject }));

    // Clear errors for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (generalError) {
      setGeneralError('');
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.house.trim()) {
      newErrors.house = 'House is required';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Fragrance name is required';
    }

    if (formData.size_oz && (isNaN(Number(formData.size_oz)) || Number(formData.size_oz) < 0)) {
      newErrors.size_oz = 'Size in oz must be a valid positive number';
    }

    if (formData.size_ml && (isNaN(Number(formData.size_ml)) || Number(formData.size_ml) < 0)) {
      newErrors.size_ml = 'Size in ml must be a valid positive number';
    }

    if (formData.price && (isNaN(Number(formData.price)) || Number(formData.price) < 0)) {
      newErrors.price = 'Price must be a valid positive number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Prepare submission data
    const submissionData = {
      ...formData,
      size_oz: formData.size_oz && formData.size_oz > 0 ? formData.size_oz : undefined,
      size_ml: formData.size_ml && formData.size_ml > 0 ? formData.size_ml : undefined,
      price: formData.price && formData.price > 0 ? formData.price : undefined,
      notes_top: formData.notes_top.trim() || undefined,
      notes_middle: formData.notes_middle.trim() || undefined,
      notes_base: formData.notes_base.trim() || undefined,
      retailer: formData.retailer.trim() || undefined,
      retailer_link: formData.retailer_link.trim() || undefined,
      notes: formData.notes.trim() || undefined
    };

    const result = item 
      ? await updateWishlistItem(item.id, submissionData)
      : await createWishlistItem(submissionData);

    if (result.success) {
      onSuccess();
      onClose();
    } else {
      setGeneralError(result.message);
    }
  };

  const isEditing = !!item;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-8">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isEditing ? 'Edit Wishlist Item' : 'Add to Wishlist'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-2xl"
            >
              ×
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* General Error */}
            {generalError && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3"
              >
                <p className="text-red-600 dark:text-red-400 text-sm">{generalError}</p>
              </motion.div>
            )}

            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                name="house"
                type="text"
                label="House *"
                placeholder="e.g., Chanel, Dior, Tom Ford"
                value={formData.house}
                onChange={handleInputChange}
                error={errors.house}
                disabled={isLoading}
              />
              <Input
                name="name"
                type="text"
                label="Fragrance Name *"
                placeholder="e.g., Bleu de Chanel, Sauvage"
                value={formData.name}
                onChange={handleInputChange}
                error={errors.name}
                disabled={isLoading}
              />
            </div>

            {/* Size and Concentration */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Concentration
                </label>
                <select
                  name="concentration"
                  value={formData.concentration}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Select concentration</option>
                  <option value="EdC">Eau de Cologne</option>
                  <option value="EdT">Eau de Toilette</option>
                  <option value="EdP">Eau de Parfum</option>
                  <option value="Parfum">Parfum</option>
                  <option value="Elixir">Elixir</option>
                  <option value="Extrait">Extrait de Parfum</option>
                </select>
              </div>
              <Input
                name="size_oz"
                type="number"
                step="0.01"
                label="Size (oz)"
                placeholder="e.g., 3.4"
                value={formData.size_oz}
                onChange={handleInputChange}
                error={errors.size_oz}
                disabled={isLoading}
              />
              <Input
                name="size_ml"
                type="number"
                step="0.1"
                label="Size (ml)"
                placeholder="e.g., 100"
                value={formData.size_ml}
                onChange={handleInputChange}
                error={errors.size_ml}
                disabled={isLoading}
              />
            </div>

            {/* Price and Currency */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                name="price"
                type="number"
                step="0.01"
                label="Price"
                placeholder="e.g., 120.00"
                value={formData.price}
                onChange={handleInputChange}
                error={errors.price}
                disabled={isLoading}
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Currency
                </label>
                <select
                  name="currency"
                  value={formData.currency}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {SUPPORTED_CURRENCIES.map(currency => (
                    <option key={currency.code} value={currency.code}>
                      {currency.symbol} {currency.name} ({currency.code})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Retailer Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                name="retailer"
                type="text"
                label="Retailer"
                placeholder="e.g., Sephora, FragranceX"
                value={formData.retailer}
                onChange={handleInputChange}
                error={errors.retailer}
                disabled={isLoading}
              />
              <Input
                name="retailer_link"
                type="url"
                label="Retailer Link"
                placeholder="https://..."
                value={formData.retailer_link}
                onChange={handleInputChange}
                error={errors.retailer_link}
                disabled={isLoading}
              />
            </div>

            {/* Notes */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Fragrance Notes
              </h3>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Top Notes
                  </label>
                  <textarea
                    name="notes_top"
                    placeholder="e.g., Bergamot, Lemon, Pink Pepper"
                    value={formData.notes_top}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Middle Notes
                  </label>
                  <textarea
                    name="notes_middle"
                    placeholder="e.g., Rose, Jasmine, Geranium"
                    value={formData.notes_middle}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Base Notes
                  </label>
                  <textarea
                    name="notes_base"
                    placeholder="e.g., Sandalwood, Vanilla, Musk"
                    value={formData.notes_base}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                  />
                </div>
              </div>
            </div>

            {/* Personal Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Personal Notes
              </label>
              <textarea
                name="notes"
                placeholder="Your thoughts, why you want this fragrance, occasions, etc."
                value={formData.notes}
                onChange={handleInputChange}
                disabled={isLoading}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
              />
            </div>

            {/* Priority and Gift Idea */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Priority
                </label>
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="low">🟢 Low Priority</option>
                  <option value="medium">🟡 Medium Priority</option>
                  <option value="high">🔴 High Priority</option>
                </select>
              </div>
              <div className="flex items-center justify-center">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_gift_idea"
                    checked={formData.is_gift_idea}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    className="rounded border-gray-300 text-pink-600 focus:ring-pink-500"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    🎁 Mark as gift idea
                  </span>
                </label>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                isLoading={isLoading}
                disabled={isLoading}
              >
                {isLoading 
                  ? (item ? 'Updating...' : 'Adding...')
                  : (item ? 'Update Item' : 'Add to Wishlist')
                }
              </Button>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default WishlistForm;
